import React, { memo, useMemo } from 'react';
import styled from '@emotion/styled';
import { ThemeProvider } from '@emotion/react';
import { transparentize } from 'polished';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { useDeviceSize } from '@vmos2/providers';

import { useComponentTheme } from '../useComponentTheme';
import { Image } from '../image/Image';
import { IconButton } from '../buttons/IconButton';
import { withSpacingStyles } from '../withSpacingStyles';

type Props = {
  backgroundColor?: string,
  children?: React.Node,
  defaultBorderColor?: string,
  name?: string,
  uuid?: string,
  active: boolean,
  onClick: Function,
  subtitle?: string,
  hasImage: boolean,
  overlay?: React.Node,
  disabled?: boolean,
  numberPrefix?: boolean,
  className?: string,
  placeholder?: string,
  imageHeight?: string,
  imageSrc?: string,
  handleInfoClick?: Function,
  mode?: 'MenuCard' | 'MealCard' | 'UpsellCard' | 'ReorderCard',
  dataTest: Object,
  footerLeft?: React.Node,
  footerRight?: React.Node,
  loyaltyPoints?: React.Node,
  description?: string,
  hideDescription: boolean,
  scope?: string,
  servings?: string,
  content?: React.Node,
  externalImageSrc?: string,
  overlayText?: string,
  overlayMode?: string,
  justifyContent?: string,
};

const _Card = memo(
  ({
    backgroundColor,
    children,
    defaultBorderColor,
    name,
    uuid,
    active,
    onClick,
    subtitle,
    hasImage,
    disabled,
    overlay,
    numberPrefix,
    className,
    placeholder,
    imageHeight,
    imageSrc,
    handleInfoClick,
    mode,
    dataTest,
    footerLeft,
    footerRight,
    description,
    hideDescription,
    scope,
    loyaltyPoints,
    servings,
    content,
    overlayText,
    overlayMode,
    externalImageSrc,
    justifyContent,
    ...rest
  }: Props) => {
    const { isMobile } = useDeviceSize();
    const componentTheme = useComponentTheme(theme => theme.components.Card, mode);

    const themeProviderValue = useMemo(() => ({
      componentTheme: {
        ...componentTheme,
        Container: {
          ...componentTheme.Container,
          ...(backgroundColor && { backgroundColor }),
          ...(defaultBorderColor && { defaultBorderColor }),
        },
      },
    }), [backgroundColor, componentTheme, defaultBorderColor]);

    const showFooterRight = !!footerRight && themeProviderValue.componentTheme.Footer.RightSide.visible;

    return (
      <ThemeProvider theme={themeProviderValue}>
        <Container
          active={active}
          data-test="card"
          disabled={disabled}
          onClick={e => !disabled && onClick(e, uuid)}
          hasImage={hasImage}
          className={className}
          componentTheme={themeProviderValue.componentTheme}
          {...omit(dataTest, 'data-test-card-name', 'data-test-card-info')}
          {...rest}
        >
          {children}

          {hasImage && (
            <ImageContainer imageHeight={imageHeight}>
              {/* todo: please use Overlay name instead of SoldOut in theme when you will add positioning for text  */}
              <StyledImage
                scope={scope}
                uuid={uuid}
                alt={name}
                placeholder={placeholder}
                src={imageSrc}
                externalSrc={externalImageSrc}
                imageHeight={imageHeight}
                sizes={isMobile ? '50vw' : '33vw'}
              />

              {overlayText ? (
                <Overlay component={componentTheme[`Overlay:${overlayMode}`]}>
                  <Label component={componentTheme[`Overlay:${overlayMode}`].Label}>{overlayText}</Label>
                </Overlay>
              ) : (
                !!overlay && active && <ActionWrapper>{overlay}</ActionWrapper>
              )}

              {handleInfoClick && componentTheme.Container.showInfoButton && (
                <InfoButtonWrapper
                  onClick={e => {
                    e.stopPropagation();
                    handleInfoClick();
                  }}
                  icon="Info"
                  mode="light"
                  size="xsmall"
                />
              )}
              {loyaltyPoints}
            </ImageContainer>
          )}

          {!hideDescription && (
            <Content>
              <Header justifyContent={justifyContent}>
                {name && <Title data-test={get(dataTest, 'data-test-card-name')}>{name}</Title>}

                {!hasImage && overlayText && (
                  <Label component={componentTheme[`Overlay:${overlayMode}`].Label}>{overlayText}</Label>
                )}

                {content}
              </Header>

              {(footerLeft || showFooterRight) && (
                <Footer data-test={get(dataTest, 'data-test-card-info')}>
                  {footerLeft && <LeftSide>{footerLeft}</LeftSide>}
                  {showFooterRight && <RightSide>{footerRight}</RightSide>}
                </Footer>
              )}
            </Content>
          )}
        </Container>
      </ThemeProvider>
    );
  }
);

export const Card = withSpacingStyles(_Card);

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  ${({ imageHeight }) => imageHeight ? `height: ${imageHeight};` : 'padding-top: 100%;'}
  border-radius: ${({ theme: { componentTheme } }) => componentTheme.Container.borderRadius};
  border-bottom-right-radius: ${({ theme: { componentTheme } }) => componentTheme.Image.borderBottomRadius};
  border-bottom-left-radius: ${({ theme: { componentTheme } }) => componentTheme.Image.borderBottomRadius};
  overflow: hidden;
`;

const Container = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => disabled && 0.25};
  color: ${({ theme }) => theme.color.black};
  background-color: ${({ theme: { componentTheme } }) => componentTheme.Container.backgroundColor};

  ${({ theme: { componentTheme: { Container: { boxShadow, defaultBorder, defaultBorderColor } } } }) => {
    if (defaultBorder && defaultBorderColor !== 'transparent') {
      return `
        border: ${defaultBorder};
        ${defaultBorderColor ? `border-color: ${defaultBorderColor};` : ''}
      `;
    }

    return `box-shadow: ${boxShadow};`;
  }};

  border-radius: ${({ theme: { componentTheme } }) => componentTheme.Container.borderRadius};
  overflow: hidden;
  overflow-wrap: break-word;
  ${({ borderTop, theme }) =>
    borderTop &&
    `border-top: 8px solid ${theme.componentTheme.Container.borderColor || theme.color.brand}
  `};
  ${({ theme, hasImage, active, componentTheme }) =>
    !hasImage &&
    `
      background: ${active ? componentTheme.Container.fillColor || transparentize(0.85, theme.color.brand) : theme.componentTheme.Container.backgroundColor};
      color: ${active ? theme.color.brand : theme.color.black};
      ${Label} {
        margin-top: 8px;
      }
  `};

  ${({
    active,
    componentTheme: { Container: { highlightActiveBorder, borderColor, borderRadius, borderWidth } },
    theme,
  }) =>
    active &&
    highlightActiveBorder && `
      &::after {
        position: absolute;
        z-index: 1;
        width: 100%;
        height: 100%;
        content: '';
        border: ${borderWidth} solid ${borderColor || theme.color.brand};
        border-radius: ${borderRadius};
      }
  `}

  ${({
    theme,
    componentTheme: {
      Container: { fillColor },
    },
    active,
  }) =>
    active &&
    `
    ${ImageContainer}::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: ${fillColor || theme.color.brand};
      opacity: .25;
      z-index: 1;
    }
  `};
`;

const StyledImage = styled(Image, { shouldForwardProp: prop => prop !== 'imageHeight' })`
  ${({ imageHeight }) => imageHeight
    ? `height: ${imageHeight};`
    : `
      position: absolute;
      height: 100%;
      top: 0;
    `
  }

  width: 100%;
  object-fit: cover;
  vertical-align: middle;
`;

const ActionWrapper = styled('div')`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: flex-end;
  top: 0;
  left: 0;
  z-index: 2;
  padding: 12px;

  @media (${({ theme }) => theme.breakpoint.iPadKiosk}) {
    padding: 16px;
  }
`;

const Content = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 12px ${({ theme: { componentTheme } }) => componentTheme.Content.padding};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    padding: 16px ${({ theme: { componentTheme } }) => componentTheme.Content.padding};
  }
`;

const Title = styled('h5')`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme: { componentTheme } }) => componentTheme.Title.fontWeight};
  color: ${({ theme: { componentTheme } }) => componentTheme.Title.color};
  font-family: ${({ theme }) =>
    theme.componentTheme.Title.secondaryFontFamily ? theme.typography.secondaryFontFamily : 'inherit'};
  display: -webkit-box;
  -webkit-line-clamp: ${({ theme: { componentTheme } }) => componentTheme.Title.truncateLines};
  -webkit-box-orient: vertical;
  word-wrap: break-word;
  overflow: hidden;
  line-height: normal;
  text-transform: ${({ theme: { componentTheme } }) => componentTheme.Title.textTransform};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ theme: { componentTheme } }) => componentTheme.Title.fontSize};
  }
`;

const Header = styled('header')`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: ${({ theme: { componentTheme } }) => componentTheme.Header.flexGrow || 1};
  ${({ justifyContent }) => justifyContent && `justify-content: ${justifyContent}`};

  &:not(:only-child) {
    margin-bottom: ${({ theme: { componentTheme } }) => componentTheme.Header.marginBottom};
  }
`;

const Footer = styled('footer')`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  align-items: baseline;
`;

const LeftSide = styled('span')`
  color: ${({ theme: { componentTheme } }) => componentTheme.Footer.LeftSide.color};
  font-weight: ${({ theme: { componentTheme } }) => componentTheme.Footer.LeftSide.fontWeight};
  font-size: ${({ theme }) => theme.fontSize.sm};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ theme: { componentTheme } }) => componentTheme.Footer.LeftSide.fontSize};
  }
`;

const RightSide = styled('span')`
  margin-left: auto;
  color: ${({ theme: { componentTheme } }) => componentTheme.Footer.RightSide.color};
  font-weight: ${({ theme: { componentTheme } }) => componentTheme.Footer.RightSide.fontWeight};
  font-size: ${({ theme }) => theme.fontSize.sm};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ theme: { componentTheme } }) => componentTheme.Footer.RightSide.fontSize};
  }
`;

const InfoButtonWrapper = styled(IconButton)`
  width: 24px;
  height: 24px;
  position: absolute;
  right: 8px;
  bottom: 8px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.grey};
  background-color: ${({ theme }) => theme.color.white};
  z-index: 2;

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    right: 16px;
    bottom: 16px;
  }

  @media (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    font-size: ${({ theme }) => theme.fontSize.md};
    right: 12px;
    bottom: 12px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.color.white};
  }
`;

const Overlay = styled('div')`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: ${({ component }) => transparentize(0.5, component.backgroundColor)};
  z-index: 1;
  display: flex;
  align-items: ${({ component }) => component.alignItems};
  justify-content: ${({ component }) => component.textAlign};
  padding: ${({ theme }) => theme.margins.sm};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    padding: ${({ theme }) => theme.margins.sm};
  }
`;

const Label = styled('div')`
  width: fit-content;
  height: fit-content;
  padding: 4px 8px;
  text-align: center;
  border-radius: ${({ component }) => component.borderRadius};
  background-color: ${({ component }) => component.backgroundColor};
  color: ${({ component }) => component.color};
  font-size: ${({ theme }) => theme.fontSize.sm};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ component }) => component.fontSize};
  }
`;

export const CardInlineNotes = styled('div')`
  > span {
    ::after {
      content: "  \\2022  ";
    }
    :last-child::after {
      content: "";
    }
  }
`;

Card.defaultProps = {
  calories: 0,
  dataTest: {},
  hideDescription: false,
  scope: 'grid',
  overlayMode: 'off',
};
